import React, { useEffect, useState } from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Card, CardContent } from '../ui/Card';
import { Filter, X, Calendar, DollarSign } from 'lucide-react';
import { cn } from '../../lib/utils';
export interface FilterState {
  dateRange: {
    from: string;
    to: string;
  };
  status: string[];
  amountRange: {
    min: string;
    max: string;
  };
  locationId: string;
}
interface TransactionFiltersProps {
  filters: FilterState;
  onFilterChange: (filters: FilterState) => void;
  onClearFilters: () => void;
}
export function TransactionFilters({
  filters,
  onFilterChange,
  onClearFilters
}: TransactionFiltersProps) {
  const [isOpen, setIsOpen] = useState(false);
  const handleStatusToggle = (status: string) => {
    const current = filters.status;
    const next = current.includes(status) ? current.filter(s => s !== status) : [...current, status];
    onFilterChange({
      ...filters,
      status: next
    });
  };
  const activeFilterCount = [filters.dateRange.from || filters.dateRange.to ? 1 : 0, filters.status.length > 0 ? 1 : 0, filters.amountRange.min || filters.amountRange.max ? 1 : 0, filters.locationId ? 1 : 0].reduce((a, b) => a + b, 0);
  // Close drawer on ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);
  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  return <>
      <div className="flex items-center gap-2" data-id="element-130">
        <Button variant="outline" onClick={() => setIsOpen(true)} className="gap-2" data-id="element-131">
          <Filter className="h-4 w-4" data-id="element-132" />
          Filters
          {activeFilterCount > 0 && <span className="ml-1 rounded-full bg-[#218080] text-white text-[10px] px-1.5 py-0.5" data-id="element-133">
              {activeFilterCount}
            </span>}
        </Button>
        {activeFilterCount > 0 && <Button variant="ghost" size="sm" onClick={onClearFilters} className="text-slate-500" data-id="element-134">
            Clear all
            <X className="h-3 w-3 ml-1" data-id="element-135" />
          </Button>}
      </div>

      {/* Backdrop */}
      {isOpen && <div className="fixed inset-0 bg-black/50 z-40 transition-opacity" onClick={() => setIsOpen(false)} data-id="element-136" />}

      {/* Drawer */}
      <div className={cn('fixed right-0 top-0 h-full w-full sm:w-96 bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out', isOpen ? 'translate-x-0' : 'translate-x-full')} data-id="element-137">
        <div className="flex flex-col h-full" data-id="element-138">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-slate-200" data-id="element-139">
            <div className="flex items-center gap-2" data-id="element-140">
              <Filter className="h-5 w-5 text-slate-600" data-id="element-141" />
              <h3 className="text-lg font-semibold text-slate-900" data-id="element-142">Filters</h3>
              {activeFilterCount > 0 && <span className="rounded-full bg-[#218080] text-white text-xs px-2 py-0.5" data-id="element-143">
                  {activeFilterCount}
                </span>}
            </div>
            <Button variant="ghost" size="icon" onClick={() => setIsOpen(false)} data-id="element-144">
              <X className="h-5 w-5" data-id="element-145" />
            </Button>
          </div>

          {/* Filter Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-6" data-id="element-146">
            {/* Date Range */}
            <div className="space-y-3" data-id="element-147">
              <label className="text-sm font-medium text-slate-700 flex items-center gap-2" data-id="element-148">
                <Calendar className="h-4 w-4" data-id="element-149" /> Date Range
              </label>
              <div className="space-y-2" data-id="element-150">
                <Input type="date" label="From" value={filters.dateRange.from} onChange={e => onFilterChange({
                ...filters,
                dateRange: {
                  ...filters.dateRange,
                  from: e.target.value
                }
              })} data-id="element-151" />
                <Input type="date" label="To" value={filters.dateRange.to} onChange={e => onFilterChange({
                ...filters,
                dateRange: {
                  ...filters.dateRange,
                  to: e.target.value
                }
              })} data-id="element-152" />
              </div>
              <div className="flex gap-2 flex-wrap" data-id="element-153">
                {['7d', '30d', '90d'].map(d => <button key={d} onClick={() => {
                const to = new Date().toISOString().split('T')[0];
                const from = new Date(Date.now() - parseInt(d) * 86400000).toISOString().split('T')[0];
                onFilterChange({
                  ...filters,
                  dateRange: {
                    from,
                    to
                  }
                });
              }} className="text-xs bg-slate-100 border border-slate-200 px-3 py-1.5 rounded hover:bg-slate-200 transition-colors" data-id="element-154">
                    Last {d.replace('d', ' days')}
                  </button>)}
              </div>
            </div>

            {/* Status */}
            <div className="space-y-3" data-id="element-155">
              <label className="text-sm font-medium text-slate-700" data-id="element-156">
                Status
              </label>
              <div className="space-y-2" data-id="element-157">
                {['pending', 'processed', 'paid', 'failed'].map(status => <label key={status} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-50 transition-colors" data-id="element-158">
                    <input type="checkbox" checked={filters.status.includes(status)} onChange={() => handleStatusToggle(status)} className="rounded border-slate-300 text-[#218080] focus:ring-[#218080] h-4 w-4" data-id="element-159" />
                    <span className="text-sm capitalize text-slate-700 font-medium" data-id="element-160">
                      {status}
                    </span>
                  </label>)}
              </div>
            </div>

            {/* Amount Range */}
            <div className="space-y-3" data-id="element-161">
              <label className="text-sm font-medium text-slate-700 flex items-center gap-2" data-id="element-162">
                <DollarSign className="h-4 w-4" data-id="element-163" /> Amount Range
              </label>
              <div className="space-y-2" data-id="element-164">
                <Input type="number" label="Minimum" placeholder="0" value={filters.amountRange.min} onChange={e => onFilterChange({
                ...filters,
                amountRange: {
                  ...filters.amountRange,
                  min: e.target.value
                }
              })} data-id="element-165" />
                <Input type="number" label="Maximum" placeholder="No limit" value={filters.amountRange.max} onChange={e => onFilterChange({
                ...filters,
                amountRange: {
                  ...filters.amountRange,
                  max: e.target.value
                }
              })} data-id="element-166" />
              </div>
            </div>

            {/* Location */}
            <div className="space-y-3" data-id="element-167">
              <label className="text-sm font-medium text-slate-700" data-id="element-168">
                Location
              </label>
              <Select value={filters.locationId} onChange={e => onFilterChange({
              ...filters,
              locationId: e.target.value
            })} options={[{
              value: '',
              label: 'All Locations'
            }, {
              value: 'loc_1',
              label: 'Head Office - New York'
            }, {
              value: 'loc_2',
              label: 'Branch - Brooklyn'
            }, {
              value: 'loc_3',
              label: 'Branch - Queens'
            }, {
              value: 'loc_4',
              label: 'Branch - Staten Island'
            }]} data-id="element-169" />
            </div>
          </div>

          {/* Footer Actions */}
          <div className="p-4 border-t border-slate-200 bg-slate-50 space-y-2" data-id="element-170">
            <Button onClick={() => setIsOpen(false)} className="w-full bg-[#218080] hover:bg-[#1a6666]" data-id="element-171">
              Apply Filters
            </Button>
            <Button variant="outline" onClick={() => {
            onClearFilters();
            setIsOpen(false);
          }} className="w-full" data-id="element-172">
              Clear All Filters
            </Button>
          </div>
        </div>
      </div>
    </>;
}